import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Users2, Phone, Mail, Pencil, Trash2, Merge, X as XIcon, ArrowRight, ChevronDown, ChevronRight, Eye, KeyRound, UserPlus, ShieldCheck, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { useDebounce } from "@/lib/useDebounce";

const Guardians = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 250);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // {guardian_id: students[]}

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);

  // Portal-account provisioning
  const [credsDlg, setCredsDlg] = useState(null); // {mode, guardian, email, temporary_password, email_sent, synthetic_email}
  const [busyId, setBusyId] = useState(null);

  const canManage = ["super_admin", "director", "receptionist"].includes(user?.role) ||
                    (user?.roles || []).some(r => ["super_admin","director","receptionist"].includes(r));
  const canMerge = ["super_admin", "director"].includes(user?.role) ||
                   (user?.roles || []).some(r => ["super_admin","director"].includes(r));

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dq.trim()) params.q = dq.trim();
      const { data } = await api.get("/guardians", { params });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dq]);

  // Auto-detect duplicates client-side by phone_norm or email_norm matches across list
  const duplicates = useMemo(() => {
    const byKey = {};
    list.forEach(g => {
      if (g.phone_norm) (byKey[`p:${g.phone_norm}`] = byKey[`p:${g.phone_norm}`] || []).push(g);
      if (g.email_norm) (byKey[`e:${g.email_norm}`] = byKey[`e:${g.email_norm}`] || []).push(g);
    });
    const groups = Object.values(byKey).filter(arr => arr.length > 1);
    // dedupe groups containing same ids
    const seen = new Set();
    const out = [];
    for (const g of groups) {
      const key = g.map(x => x.id).sort().join("|");
      if (!seen.has(key)) { seen.add(key); out.push(g); }
    }
    return out;
  }, [list]);

  const toggleExpand = async (g) => {
    if (expanded[g.id]) {
      setExpanded(e => ({ ...e, [g.id]: null }));
      return;
    }
    try {
      const { data } = await api.get(`/guardians/${g.id}`);
      setExpanded(e => ({ ...e, [g.id]: data.students || [] }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (g) => {
    setEditForm({
      id: g.id,
      full_name: g.full_name || "",
      relationship: g.relationship || "",
      phone: g.phone || "",
      phone_alt: g.phone_alt || "",
      email: g.email || "",
      occupation: g.occupation || "",
      address: g.address || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.full_name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { id, ...payload } = editForm;
      await api.patch(`/guardians/${id}`, payload);
      toast.success("Guardian updated");
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (g.student_count > 0) return toast.error(`Cannot delete — ${g.student_count} student(s) still linked.`);
    if (!window.confirm(`Delete guardian "${g.full_name}"?`)) return;
    try {
      await api.delete(`/guardians/${g.id}`);
      toast.success("Guardian deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openMerge = (g) => {
    setMergeSource(g);
    setMergeTargetId("");
    setMergeOpen(true);
  };

  const createLogin = async (g) => {
    if (busyId) return;
    setBusyId(g.id);
    try {
      const { data } = await api.post(`/guardians/${g.id}/create-login`);
      setCredsDlg({ mode: "create", guardian: g, ...data });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const resetLogin = async (g) => {
    if (!window.confirm(`Reset portal password for ${g.full_name}? Their current password will stop working.`)) return;
    setBusyId(g.id);
    try {
      const { data } = await api.post(`/guardians/${g.id}/reset-login-password`);
      setCredsDlg({ mode: "reset", guardian: g, ...data, email_sent: false });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const doMerge = async () => {
    if (!mergeTargetId) return toast.error("Pick the target guardian to merge into");
    if (mergeTargetId === mergeSource.id) return toast.error("Source and target must be different");
    if (!window.confirm("Merge will move all children of the source into the target and DELETE the source record. Continue?")) return;
    setMergeBusy(true);
    try {
      const { data } = await api.post(`/guardians/${mergeSource.id}/merge-into/${mergeTargetId}`);
      toast.success(`Merged. ${data.moved} child link(s) moved, ${data.skipped} de-duplicated.`);
      setMergeOpen(false);
      setMergeSource(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMergeBusy(false); }
  };

  // Merge-target candidates: same phone/email or matching name segments (and not the source itself)
  const mergeCandidates = useMemo(() => {
    if (!mergeSource) return [];
    const srcPhone = mergeSource.phone_norm;
    const srcEmail = mergeSource.email_norm;
    return list.filter(g => g.id !== mergeSource.id && (
      (srcPhone && g.phone_norm === srcPhone) ||
      (srcEmail && g.email_norm === srcEmail) ||
      (g.full_name && mergeSource.full_name && g.full_name.toLowerCase().split(" ").some(p => mergeSource.full_name.toLowerCase().includes(p)))
    ));
  }, [mergeSource, list]);

  return (
    <div data-testid="guardians-page">
      <PageHeader
        overline="Family"
        title="Guardians & Families"
        description="Parents and guardians linked to students. Merge duplicates here to keep siblings together."
        actions={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone or email…"
              className="pl-9 h-10 w-80 bg-white"
              data-testid="guardians-search"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1B2C5A]" aria-label="Clear search">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        }
      />

      {/* Duplicate suggestions banner */}
      {duplicates.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/60 mb-4 p-4" data-testid="dup-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">
              <Merge className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900">
                {duplicates.length} possible duplicate group{duplicates.length !== 1 ? "s" : ""} detected
              </div>
              <p className="text-xs text-amber-800 mt-0.5">Guardians sharing a phone or email are likely the same person. Merge them to keep siblings linked.</p>
              <div className="mt-3 space-y-2">
                {duplicates.slice(0, 5).map((grp, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`dup-group-${i}`}>
                    {grp.map((g, idx) => (
                      <span key={g.id} className="inline-flex items-center gap-1">
                        <span className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-900">
                          <strong className="text-[#1B2C5A]">{g.full_name}</strong>
                          <span className="text-[#475569]"> · {g.student_count} child{g.student_count !== 1 ? "ren" : ""}</span>
                        </span>
                        {idx < grp.length - 1 && <ArrowRight className="w-3 h-3 text-amber-700" />}
                      </span>
                    ))}
                    {canMerge && (
                      <Button size="sm" variant="ghost" onClick={() => openMerge(grp[0])} className="text-amber-900 hover:text-amber-700 text-xs h-7" data-testid={`dup-resolve-${i}`}>
                        <Merge className="w-3 h-3 mr-1" /> Resolve
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {list.length === 0 ? (
        <EmptyState message={loading ? "Loading…" : (dq ? "No guardians match this search." : "No guardians yet. Admit a student with a guardian to create one.")} testId="guardians-empty" />
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead className="w-8"></TableHead>
                <TableHead>Guardian</TableHead>
                <TableHead>Relationship</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-center">Children</TableHead>
                <TableHead className="text-center">Portal</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map(g => (
                <Fragment key={g.id}>
                  <TableRow data-testid={`guardian-row-${g.id}`}>
                    <TableCell className="pr-0">
                      <Button size="icon" variant="ghost" onClick={() => toggleExpand(g)} className="h-7 w-7" data-testid={`expand-${g.id}`}>
                        {expanded[g.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-display shrink-0">
                          {(g.full_name?.[0] || "G").toUpperCase()}
                        </span>
                        <span className="font-medium text-[#1B2C5A]">{g.full_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#475569]">{g.relationship || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {g.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-[#9DCD3F]" /> {g.phone}</span> : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {g.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-[#9DCD3F]" /> {g.email}</span> : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={"text-xs px-2 py-0.5 rounded-full " + (g.student_count > 0 ? "bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium" : "bg-slate-100 text-slate-600")}>
                        <Users2 className="w-3 h-3 inline mr-1" /> {g.student_count}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {g.user_id ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`portal-yes-${g.id}`}>
                          <ShieldCheck className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200" data-testid={`portal-no-${g.id}`}>No login</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        {canManage && !g.user_id && (
                          <Button size="sm" variant="ghost" onClick={() => createLogin(g)} disabled={busyId === g.id} className="text-emerald-700" title="Create parent portal login" data-testid={`create-login-${g.id}`}>
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                        {canManage && g.user_id && (
                          <Button size="sm" variant="ghost" onClick={() => resetLogin(g)} disabled={busyId === g.id} className="text-amber-700" title="Reset portal password" data-testid={`reset-login-${g.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        {canManage && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(g)} className="text-[#1B2C5A]" data-testid={`edit-${g.id}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                        )}
                        {canMerge && (
                          <Button size="sm" variant="ghost" onClick={() => openMerge(g)} className="text-amber-700" data-testid={`merge-${g.id}`} title="Merge into another guardian"><Merge className="w-4 h-4" /></Button>
                        )}
                        {canManage && (
                          <Button size="sm" variant="ghost" onClick={() => remove(g)} className="text-red-700" data-testid={`delete-${g.id}`} title={g.student_count > 0 ? "Cannot delete — children linked" : "Delete"} disabled={g.student_count > 0}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expanded[g.id] && (
                    <TableRow key={g.id + "_exp"} className="bg-[#FDFBF7]/60">
                      <TableCell colSpan={8} className="py-3 px-6">
                        {expanded[g.id].length === 0 ? (
                          <p className="text-sm text-[#475569]">No children linked yet.</p>
                        ) : (
                          <div className="flex flex-wrap gap-2" data-testid={`children-of-${g.id}`}>
                            {expanded[g.id].map(c => (
                              <button key={c.id} onClick={() => navigate(`/app/students/${c.id}`)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] hover:border-[#9DCD3F] hover:bg-[#9DCD3F]/10 transition" data-testid={`child-${c.id}`}>
                                {c.photo ? (
                                  <img src={c.photo} alt={c.full_name} className="w-5 h-5 rounded-full object-cover" />
                                ) : (
                                  <span className="w-5 h-5 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-[9px]">{(c.full_name?.[0] || "?").toUpperCase()}</span>
                                )}
                                <span className="text-sm text-[#1B2C5A]">{c.full_name}</span>
                                <span className="text-xs font-mono text-[#475569]">{c.admission_no}</span>
                                {c.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#9DCD3F]/30 text-[#1B2C5A]">primary</span>}
                                <Eye className="w-3 h-3 text-[#9DCD3F]" />
                              </button>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Pencil className="w-4 h-4 text-[#9DCD3F]" /> Edit Guardian
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Full Name *"><Input value={editForm.full_name || ""} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="g-edit-name" /></Fld>
            <Fld label="Relationship"><Input value={editForm.relationship || ""} onChange={(e) => setEditForm({ ...editForm, relationship: e.target.value })} placeholder="Father / Mother / …" /></Fld>
            <Fld label="Phone (Primary)"><Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="g-edit-phone" /></Fld>
            <Fld label="Phone (Alt)"><Input value={editForm.phone_alt || ""} onChange={(e) => setEditForm({ ...editForm, phone_alt: e.target.value })} /></Fld>
            <div className="col-span-2"><Fld label="Email"><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="g-edit-email" /></Fld></div>
            <Fld label="Occupation"><Input value={editForm.occupation || ""} onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })} /></Fld>
            <div className="col-span-2"><Fld label="Address"><Input value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></Fld></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="g-edit-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog (create/reset) */}
      <Dialog open={!!credsDlg} onOpenChange={(v) => !v && setCredsDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Parent Portal {credsDlg?.mode === "reset" ? "Password Reset" : "Login Created"}
            </DialogTitle>
          </DialogHeader>
          {credsDlg && (
            <div className="space-y-3" data-testid="creds-dialog">
              <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900">
                <strong>Copy these credentials now.</strong> This is the only time the temporary password will be shown. The parent will be forced to change it on first login.
              </div>
              <CredRow label="Parent" value={credsDlg.guardian?.full_name} />
              <CredRow label="Email" value={credsDlg.email} testId="creds-email" />
              <CredRow label="Temporary Password" value={credsDlg.temporary_password} mono testId="creds-password" />
              <CredRow label="Portal URL" value={`${window.location.origin}/login`} />

              {credsDlg.synthetic_email && (
                <p className="text-xs text-amber-800 italic">⚠ Guardian had no email on file, so a temporary internal address was generated (@parents.dfa.local). The parent won&apos;t be able to receive emails on it — hand the credentials over in person and set a real email when possible.</p>
              )}
              {credsDlg.email_sent && <p className="text-xs text-emerald-700">✓ A copy has been emailed to the parent.</p>}
              {credsDlg.email_error && <p className="text-xs text-red-700">Email delivery failed: {credsDlg.email_error}</p>}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => {
                    const text = `Darussalam Foundation Academy — Parent Portal\n\nURL: ${window.location.origin}/login\nEmail: ${credsDlg.email}\nTemporary Password: ${credsDlg.temporary_password}\n\nYou will be asked to change the password when you first sign in.`;
                    navigator.clipboard.writeText(text).then(() => toast.success("Credentials copied"));
                  }}
                  className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
                  data-testid="creds-copy"
                ><Copy className="w-4 h-4 mr-1.5" /> Copy All</Button>
                {!credsDlg.synthetic_email && (
                  <a
                    href={`mailto:${credsDlg.email}?subject=${encodeURIComponent("Your Darussalam Parent Portal Access")}&body=${encodeURIComponent(`Assalāmu ʿalaykum ${credsDlg.guardian?.full_name || ""},\n\nYour parent portal login has been created.\n\nURL: ${window.location.origin}/login\nEmail: ${credsDlg.email}\nTemporary Password: ${credsDlg.temporary_password}\n\nYou will be asked to change the password when you first sign in.\n\n— Darussalam Foundation Academy`)}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#9DCD3F] text-[#1B2C5A] hover:bg-[#9DCD3F]/10 text-sm"
                    data-testid="creds-mailto"
                  ><Send className="w-4 h-4" /> Open Email Draft</a>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredsDlg(null)} data-testid="creds-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
              <Merge className="w-4 h-4 text-amber-600" /> Merge Guardians
            </DialogTitle>
          </DialogHeader>
          {mergeSource && (
            <div className="space-y-3">
              <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
                <div className="text-xs uppercase tracking-wider text-red-700 font-medium">Source (will be deleted)</div>
                <div className="text-sm font-medium text-[#1B2C5A] mt-0.5">{mergeSource.full_name}</div>
                <div className="text-xs text-[#475569] mt-0.5 flex gap-3">
                  {mergeSource.phone && <span>{mergeSource.phone}</span>}
                  {mergeSource.email && <span>· {mergeSource.email}</span>}
                  <span>· {mergeSource.student_count} child{mergeSource.student_count !== 1 ? "ren" : ""}</span>
                </div>
              </div>
              <div className="flex justify-center"><ArrowRight className="w-4 h-4 text-[#9DCD3F]" /></div>
              <div>
                <Label className="text-xs text-[#475569] mb-1.5 block">Target — keep this guardian, move all children to them:</Label>
                {mergeCandidates.length === 0 ? (
                  <p className="text-xs text-amber-700 italic">No likely matches found. Search and pick manually:</p>
                ) : null}
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {(mergeCandidates.length > 0 ? mergeCandidates : list.filter(g => g.id !== mergeSource.id)).slice(0, 20).map(g => (
                    <button key={g.id} onClick={() => setMergeTargetId(g.id)} className={"w-full text-left rounded-lg border p-2.5 transition " + (mergeTargetId === g.id ? "border-[#9DCD3F] bg-[#9DCD3F]/10" : "border-[#E2E8F0] bg-white hover:border-[#9DCD3F]/40")} data-testid={`merge-target-${g.id}`}>
                      <div className="text-sm font-medium text-[#1B2C5A]">{g.full_name}</div>
                      <div className="text-xs text-[#475569] mt-0.5 flex gap-3 flex-wrap">
                        {g.phone && <span>{g.phone}</span>}
                        {g.email && <span>· {g.email}</span>}
                        <span>· {g.student_count} child{g.student_count !== 1 ? "ren" : ""}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={mergeBusy}>Cancel</Button>
            <Button onClick={doMerge} disabled={mergeBusy || !mergeTargetId} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="merge-confirm">
              {mergeBusy ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-xs">{label}</Label>{children}</div>
);

const CredRow = ({ label, value, mono, testId }) => (
  <div className="grid grid-cols-[110px_1fr] gap-3 items-center">
    <div className="text-xs text-[#475569] uppercase tracking-wider">{label}</div>
    <div className={"text-sm text-[#1B2C5A] break-all " + (mono ? "font-mono font-semibold" : "")} data-testid={testId}>{value}</div>
  </div>
);

export default Guardians;
